import React, { useState, useEffect, useContext } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Typography,
  DatePicker,
  Select,
  Button,
  Image,
  Upload,
} from "antd";
import moment from "moment";
import { DeleteOutlined } from "@ant-design/icons";

import Popup from "../../../components/Popup";
import { DateFormat, errorMessage, getApi } from "../../../helpers";
import PreviewBackdrop from "../../../components/PreviewBackdrop";
import { StoreContext } from "../../../context";
import "./index.css";

export default function BackdropPopup({
  visible = false,
  title = "",
  data = {},
  cancelHandler,
  saveHandler,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [backdropImageFile, setBackdropImageFile] = useState(
    data.poster_link ? { url: data.poster_link } : {}
  );
  const [bannerList, setBannerList] = useState([]);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedStore, setSelectedStore] = useState(storeId || data.store_id || undefined);
  const [colour1, setColour1] = useState(data.colour_1 || "#FFF0F2");
  const [colour2, setColour2] = useState(data.colour_2 || "#FF4C64");
  const [iconColour, setIconColour] = useState(data.icon_colour || "#000000");
  const [form] = Form.useForm();

  // Function to detect content type from file
  const getContentType = (file) => {
    const fileType = file.type;
    if (fileType.startsWith("image/")) {
      if (fileType === "image/gif") {
        return "image/gif";
      }
      return fileType; // image/png, image/jpeg, etc.
    } else if (fileType.startsWith("video/")) {
      return fileType; // video/mp4, etc.
    }
    return "image/png"; // default
  };

  useEffect(() => {
    getStores();
  }, []);

  useEffect(() => {
    if (storeId) {
      form.setFieldsValue({ store_id: storeId });
      setSelectedStore(storeId);
    }
  }, [storeId, form]);

  useEffect(() => {
    getBanners(storeId || selectedStore);
  }, [storeId, selectedStore]);

  useEffect(() => {
    // When bannerList is loaded and we have a banner_id from data, set the selected banner
    if (bannerList.length > 0 && data.banner_id) {
      const banner = bannerList.find((b) => b.banner_id === data.banner_id);
      if (banner) {
        setSelectedBanner(banner);
      }
    }
  }, [bannerList, data.banner_id]);

  async function getStores() {
    const res = await getApi({}, "getStores", "dashboard/");
    if (res.data && res.data.length) {
      setStores(res.data);
    }
  }

  async function getBanners(filterStoreId) {
    setLoading(true);
    let payload = { size: 100, type: "admin", is_active: 1 };
    if (filterStoreId) payload = { ...payload, store_id: filterStoreId };

    const res = await getApi(payload, "getBanner");
    if (res.data && res.data.length) {
      setBannerList(res.data);
    } else {
      setBannerList([]);
      setSelectedBanner(null);
    }
    setLoading(false);
  }

  function saveBackdrop() {
    form
      .validateFields()
      .then((values) => {
        if (!backdropImageFile.url && !selectedBanner) {
          errorMessage("Please upload a backdrop image or select a banner!");
          return;
        }

        // Auto-detect content type from file
        let contentType = data.content_type || "image/png";
        if (backdropImageFile.file) {
          contentType = getContentType(backdropImageFile.file);
        }

        values = {
          ...values,
          start_date: DateFormat(values.validity[0], "YYYY-MM-DD HH:mm:ss"),
          end_date: DateFormat(values.validity[1], "YYYY-MM-DD HH:mm:ss"),
          colour_1: colour1,
          colour_2: colour2,
          icon_colour: iconColour,
          content_type: contentType,
          is_active: 1,
        };

        if (selectedBanner) {
          values.banner_id = selectedBanner.banner_id;
        }

        if (data.poster_link && backdropImageFile.file) {
          values = { ...values, file: backdropImageFile.file, delete: true };
        } else if (backdropImageFile.file) {
          values = { ...values, file: backdropImageFile.file };
        }

        saveHandler(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }

  const handleBannerSelect = (bannerId) => {
    const banner = bannerList.find((b) => b.banner_id === bannerId);
    setSelectedBanner(banner);
  };

  const isVideoContent =
    data.content_type?.startsWith("video") ||
    backdropImageFile.file?.type?.startsWith("video");

  return (
    <Popup
      width="80%"
      title={title}
      visible={visible}
      cancelHandler={() => (form.resetFields(), cancelHandler())}
      preSaveCheck={() =>
        form
          .validateFields()
          .then(() => true)
          .catch(() => false)
      }
      saveHandler={saveBackdrop}
    >
      <Form
        className="backdrop-popup-form"
        requiredMark={false}
        form={form}
        layout="vertical"
        name="backdrop"
        initialValues={{ ...data, store_id: storeId || data.store_id }}
        onValuesChange={(changedValues) => {
          if (Object.prototype.hasOwnProperty.call(changedValues, "store_id")) {
            setSelectedStore(changedValues.store_id);
            setSelectedBanner(null);
          }
        }}
      >
        <Row className="backdrop-popup-layout" gutter={[24, 0]}>
          <Col xs={24} md={13} className="backdrop-popup-form-col">
            <div className="backdrop-popup-section">
              <div className="backdrop-popup-section-header">
                <Typography.Text className="backdrop-popup-section-title">
                  Backdrop Setup
                </Typography.Text>
                <Typography.Text className="backdrop-popup-section-subtitle">
                  Configure media, gradient colors, banner mapping, and validity in one place.
                </Typography.Text>
              </div>

              <Row className="backdrop-popup-fields" gutter={[12, 0]}>
              {/* Backdrop Image Upload */}
              <Col xs={24}>
                {backdropImageFile.url ? (
                  <Col xs={24}>
                    <div className="backdrop-popup-image-actions">
                      <Button
                        onClick={() => setBackdropImageFile({})}
                        size="small"
                      >
                        <Typography.Text className="backdrop-popup-remove-image-text">
                          <DeleteOutlined /> Remove {isVideoContent ? "Video" : "Image"}
                        </Typography.Text>
                      </Button>
                    </div>
                    {isVideoContent ? (
                      <video
                        width="100%"
                        className="backdrop-popup-media"
                        src={backdropImageFile.url}
                        controls
                        autoPlay
                        muted
                        loop
                      />
                    ) : (
                      <Image
                        width="100%"
                        className="backdrop-popup-media"
                        src={backdropImageFile.url}
                        alt="Backdrop"
                      />
                    )}
                  </Col>
                ) : (
                  <div className="backdrop-popup-upload-wrap">
                    <Typography.Text
                      strong
                      className="backdrop-popup-upload-title"
                    >
                      Backdrop Image (390 x 120 px)
                    </Typography.Text>
                    <Typography.Text className="backdrop-popup-upload-helper">
                      Use a clear visual with minimal text for better mobile readability.
                    </Typography.Text>
                    <Upload
                      accept="image/*,video/*"
                      listType="picture-card"
                      className="banner-uplodr"
                      beforeUpload={(file) => {
                        const isLt10M = file.size / 1024 / 1024 < 10;
                        if (!isLt10M) {
                          errorMessage("File must be smaller than 10MB!");
                        }
                        return isLt10M;
                      }}
                      customRequest={({ file, onSuccess }) => {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setBackdropImageFile({
                            file: file,
                            url: e.target.result,
                          });
                        };
                        reader.readAsDataURL(file);
                        onSuccess("Ok");
                      }}
                      showUploadList={false}
                    >
                      <div>
                        <div className="backdrop-popup-upload-caption">
                          Upload Backdrop
                          <br />
                          <span className="backdrop-popup-upload-subtext">
                            (Image, GIF, or Video)
                            <br />
                            390 x 120 px
                          </span>
                        </div>
                      </div>
                    </Upload>
                  </div>
                )}
              </Col>

              {/* Name */}
              <Col xs={24}>
                <Form.Item
                  name="name"
                  className="backdrop-popup-tight-item"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the name of backdrop!",
                    },
                  ]}
                >
                  <Input placeholder="Ex. Summer Sale 2025" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <div className="backdrop-popup-group-heading">Theme Colors</div>
              </Col>

              {/* Color Pickers Row */}
              <Col xs={24}>
                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Colour 1 (Top)" className="backdrop-popup-color-item">
                      <div className="backdrop-popup-color-control">
                        <input
                          type="color"
                          value={colour1}
                          onChange={(e) => setColour1(e.target.value)}
                          className="backdrop-popup-color-swatch"
                        />
                        <Input
                          value={colour1}
                          onChange={(e) => setColour1(e.target.value)}
                          placeholder="#FFD700"
                          className="backdrop-popup-color-input"
                        />
                      </div>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Colour 2 (Bottom)" className="backdrop-popup-color-item">
                      <div className="backdrop-popup-color-control">
                        <input
                          type="color"
                          value={colour2}
                          onChange={(e) => setColour2(e.target.value)}
                          className="backdrop-popup-color-swatch"
                        />
                        <Input
                          value={colour2}
                          onChange={(e) => setColour2(e.target.value)}
                          placeholder="#FF6347"
                          className="backdrop-popup-color-input"
                        />
                      </div>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Icon Colour" className="backdrop-popup-color-item">
                      <div className="backdrop-popup-color-control">
                        <input
                          type="color"
                          value={iconColour}
                          onChange={(e) => setIconColour(e.target.value)}
                          className="backdrop-popup-color-swatch"
                        />
                        <Input
                          value={iconColour}
                          onChange={(e) => setIconColour(e.target.value)}
                          placeholder="#000000"
                          className="backdrop-popup-color-input"
                        />
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {/* Banner Selection */}
              <Col xs={24}>
                <div className="backdrop-popup-group-heading">Banner Mapping</div>
                <Form.Item label="Select Banner (358 x 170 px)" className="backdrop-popup-tight-item">
                  <Select
                    loading={loading}
                    placeholder="Select a banner"
                    showSearch
                    allowClear
                    value={selectedBanner?.banner_id}
                    onChange={handleBannerSelect}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {bannerList.map((banner) => (
                      <Select.Option key={banner.banner_id} value={banner.banner_id}>
                        {banner.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                {selectedBanner && selectedBanner.banner_image && (
                  <div className="backdrop-popup-selected-banner-wrap">
                    <Image
                      src={selectedBanner.banner_image}
                      alt="Selected Banner"
                      className="backdrop-popup-selected-banner"
                    />
                  </div>
                )}
              </Col>

              {/* Validity */}
              <Col xs={24}>
                <div className="backdrop-popup-group-heading">Schedule</div>
                <Form.Item
                  name="validity"
                  className="backdrop-popup-tight-item"
                  label="Validity Period"
                  rules={[
                    {
                      required: true,
                      message: "Please select the validity!",
                    },
                  ]}
                  initialValue={
                    data.start_date &&
                    data.end_date && [
                      moment(data.start_date),
                      moment(data.end_date),
                    ]
                  }
                >
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    showTime
                    placeholder={["Start Date", "End Date"]}
                    format="DD-MM-YYYY HH:mm"
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name="store_id"
                  className="backdrop-popup-store-item"
                  label="Store"
                  rules={[
                    {
                      required: true,
                      message: "Please select store!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Store"
                    showSearch
                    optionFilterProp="children"
                    disabled={!!storeId}
                  >
                    {stores.map((store) => (
                      <Select.Option key={store.store_id} value={store.store_id}>
                        {store.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              </Row>
            </div>
          </Col>

          {/* Preview */}
          <Col xs={0} md={{ offset: 1, span: 10 }} className="backdrop-popup-preview-col">
            <div className="backdrop-popup-preview-wrap">
              <PreviewBackdrop
                backdropSrc={backdropImageFile.url}
                bannerSrc={selectedBanner?.banner_image}
                bannerData={selectedBanner}
                colour1={colour1}
                colour2={colour2}
                iconColour={iconColour}
                contentType={backdropImageFile.file?.type || data.content_type || "image/png"}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Popup>
  );
}
